import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Icon } from '@liftango/ui-icons';

import { useImportWizardState } from './useImportWizardState';
import FileTypeSelectionStep from './steps/FileTypeSelectionStep';
import FileUploadStep from './steps/FileUploadStep';
import ColumnMappingStep from './steps/ColumnMappingStep';
import ValidationStep from './steps/ValidationStep';

import type { ImportFileType, LayerTarget } from './types';
import type { Point, Line, Polygon, Layer, TripImportSquishMeta } from '../../context/context';
import { AppContext } from '../../context/context';
import type { RideSchema } from '../../types/simulation';
import type { Vehicle } from '../../types/vehicle';
import type { Fleet } from '../map/components/FleetMenu';
import { parseGTFSZip, parseKMLShapes, parseExcelDateTime, parseWktPolygonRing, toHHMM } from './utils/fileParsers';
import { processRidesFile } from './utils/processRidesFile';
import { logEvent } from '../../services/analytics';
import { useRides, lineToRideSchema } from '../../context/RidesContext';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { combineDateWithTime, isValidTimeFormat } from '../../utils/dateTimeUtils';

interface ImportWizardModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (
    shapes: (Point | Line | Polygon)[],
    meta?: { tripImportSquish?: TripImportSquishMeta },
    layerTarget?: LayerTarget
  ) => void;
  existingLayers: Layer[];
  droppedFile?: File | null;
}

const STEP_LABELS: Record<string, string> = {
  file_type: 'importWizard.steps.fileType',
  upload: 'importWizard.steps.upload',
  column_mapping: 'importWizard.steps.columnMapping',
  validation: 'importWizard.steps.validation',
};

const ImportWizardModal: React.FC<ImportWizardModalProps> = ({
  open,
  onClose,
  onImport,
  existingLayers,
  droppedFile,
}) => {
  const { t } = useTranslation('mapDialogs');
  const { bulkAddRides } = useRides();
  const context = useContext(AppContext);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const {
    state,
    steps,
    currentStepId,
    totalSteps,
    canProceed,
    setStep,
    nextStep,
    prevStep,
    addFiles,
    removeFile,
    setFileType,
    setColumnMapping,
    setActiveFileIndex,
    runValidation,
    reset,
  } = useImportWizardState();

  const [selectedType, setSelectedType] = useState<ImportFileType | null>(null);
  const addedDroppedFile = useRef<File | null>(null);

  // A file dropped on the map opens the wizard; skip straight to upload with it attached.
  useEffect(() => {
    if (!open || !droppedFile || addedDroppedFile.current === droppedFile) return;
    addedDroppedFile.current = droppedFile;
    addFiles([droppedFile]);
    setStep(2);
  }, [open, droppedFile, addFiles, setStep]);

  const handleClose = useCallback(() => {
    reset();
    setSelectedType(null);
    addedDroppedFile.current = null;
    setImportError(null);
    onClose();
  }, [reset, onClose]);

  const handleSelectFileType = useCallback((type: ImportFileType) => {
    setSelectedType(type);
    nextStep();
  }, [nextStep]);

  const handleImportAll = useCallback(async () => {
    setIsImporting(true);
    setImportError(null);
    const startTime = Date.now();

    try {
      for (const rawEntry of state.files) {
        const skipped = new Set(rawEntry.validationResult?.invalidRowIndexes ?? []);
        const fileEntry = skipped.size > 0
          ? { ...rawEntry, parsedRows: rawEntry.parsedRows.filter((_, i) => !skipped.has(i)) }
          : rawEntry;
        const layerTarget: LayerTarget = {
          mode: 'new',
          layerName: `Imported ${fileEntry.fileType} Layer`,
        };

        if (fileEntry.detectedType === 'gtfs') {
          const { points, lines } = await parseGTFSZip(fileEntry.file);
          onImport([...points, ...lines], undefined, layerTarget);
          logEvent({
            category: 'Layer Management',
            action: 'ImportWizardFileImported',
            label: fileEntry.fileName,
            file_type: 'gtfs',
            points_created: points.length,
            lines_created: lines.length,
            processing_time_ms: Date.now() - startTime,
          });
          continue;
        }

        if (fileEntry.detectedType === 'kml') {
          const text = await fileEntry.file.text();
          const shapes = parseKMLShapes(text);
          onImport(shapes, undefined, layerTarget);
          logEvent({
            category: 'Layer Management',
            action: 'ImportWizardFileImported',
            label: fileEntry.fileName,
            file_type: 'kml',
            shapes_created: shapes.length,
            processing_time_ms: Date.now() - startTime,
          });
          continue;
        }

        if (fileEntry.fileType === 'rides') {
          const shapes = processRidesFile(fileEntry, (key, opts) => String(t(key, opts)));
          const rideSchemas = (shapes.filter(s => s.type === 'line') as Line[])
            .map(line => lineToRideSchema(line))
            .filter((r): r is RideSchema => r !== null);

          onImport(shapes, undefined, layerTarget);
          if (rideSchemas.length > 0) {
            bulkAddRides(rideSchemas, 'merge');
          }

          logEvent({
            category: 'Layer Management',
            action: 'ImportWizardFileImported',
            label: fileEntry.fileName,
            file_type: 'rides',
            shapes_created: shapes.length,
            rows_processed: fileEntry.parsedRows.length,
            processing_time_ms: Date.now() - startTime,
          });
          continue;
        }

        if (fileEntry.fileType === 'points') {
          const shapes = processPointsFile(fileEntry, (key, opts) => String(t(key, opts)));
          onImport(shapes, undefined, layerTarget);
          logEvent({
            category: 'Layer Management',
            action: 'ImportWizardFileImported',
            label: fileEntry.fileName,
            file_type: 'points',
            shapes_created: shapes.length,
            processing_time_ms: Date.now() - startTime,
          });
          continue;
        }

        if (fileEntry.fileType === 'polygons') {
          const shapes = processPolygonsFile(fileEntry);
          onImport(shapes, undefined, layerTarget);
          logEvent({
            category: 'Layer Management',
            action: 'ImportWizardFileImported',
            label: fileEntry.fileName,
            file_type: 'polygons',
            shapes_created: shapes.length,
            processing_time_ms: Date.now() - startTime,
          });
          continue;
        }

        if (fileEntry.fileType === 'fleet_shifts') {
          const vehicles = processFleetFile(fileEntry);
          if (vehicles.length > 0 && context?.global?.setFleets) {
            const newFleet: Fleet = {
              id: `fleet-import-${uuidv4().slice(0, 8)}`,
              name: fileEntry.fileName.replace(/\.[^.]+$/, ''),
              vehicles,
            };
            context.global.setFleets(prev => [...prev, newFleet]);
          }
          logEvent({
            category: 'Layer Management',
            action: 'ImportWizardFileImported',
            label: fileEntry.fileName,
            file_type: 'fleet_shifts',
            vehicles_created: vehicles.length,
            rows_processed: fileEntry.parsedRows.length,
            processing_time_ms: Date.now() - startTime,
          });
          continue;
        }
      }

      logEvent({
        category: 'Layer Management',
        action: 'ImportWizardCompleted',
        label: `${state.files.length} files`,
        file_count: state.files.length,
        processing_time_ms: Date.now() - startTime,
      });

      handleClose();
    } catch (err: any) {
      setImportError(err.message || 'An error occurred during import.');
      logEvent({
        category: 'Layer Management',
        action: 'ImportWizardError',
        error_message: err.message,
        processing_time_ms: Date.now() - startTime,
      });
    } finally {
      setIsImporting(false);
    }
  }, [state.files, onImport, bulkAddRides, handleClose, t, context]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{t('importWizard.title')}</Typography>
        <IconButton onClick={handleClose} size="small">
          <Icon name="xmark" size={20} />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {steps.map((step, index) => (
            <Chip
              key={step.id}
              label={`${index + 1}. ${t(STEP_LABELS[step.id])}`}
              color={step.id === currentStepId ? 'primary' : step.completed ? 'success' : 'default'}
              variant={step.id === currentStepId ? 'filled' : 'outlined'}
              onClick={step.enabled ? () => setStep(index + 1) : undefined}
              size="small"
              sx={{ cursor: step.enabled ? 'pointer' : 'default' }}
            />
          ))}
        </Stack>
      </Box>

      <DialogContent sx={{ minHeight: 400 }}>
        {currentStepId === 'file_type' && (
          <FileTypeSelectionStep onSelectType={handleSelectFileType} />
        )}
        {currentStepId === 'upload' && (
          <FileUploadStep
            files={state.files}
            onAddFiles={files => addFiles(files, selectedType)}
            onRemoveFile={removeFile}
            onSetFileType={setFileType}
          />
        )}
        {currentStepId === 'column_mapping' && (
          <ColumnMappingStep
            files={state.files}
            activeFileIndex={state.activeFileIndex}
            onActiveFileIndexChange={setActiveFileIndex}
            onSetColumnMapping={setColumnMapping}
          />
        )}
        {currentStepId === 'validation' && (
          <ValidationStep
            files={state.files}
            onRunValidation={runValidation}
          />
        )}

        {importError && (
          <Box sx={{ mt: 2 }}>
            <Typography color="error">{importError}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>{t('common:cancel')}</Button>
        <Box sx={{ flex: 1 }} />
        {state.currentStep > 1 && (
          <Button onClick={prevStep} disabled={isImporting}>
            {t('importWizard.back')}
          </Button>
        )}
        {currentStepId !== 'validation' ? (
          <Button
            variant="contained"
            onClick={nextStep}
            disabled={!canProceed}
          >
            {t('importWizard.next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleImportAll}
            disabled={isImporting || !canProceed}
            startIcon={isImporting ? <CircularProgress size={16} /> : undefined}
          >
            {isImporting ? t('importWizard.importing') : t('importWizard.importAll')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

function processFleetFile(
  fileEntry: { parsedRows: Record<string, unknown>[]; columnMapping: Record<string, string> }
): Vehicle[] {
  const vehicles: Vehicle[] = [];
  const { parsedRows, columnMapping } = fileEntry;

  parsedRows.forEach((row) => {
    const vehicleId = String(row[columnMapping.vehicleId] ?? '').trim();
    if (!vehicleId) return;

    const startLat = parseFloat(String(row[columnMapping.startLat] ?? ''));
    const startLng = parseFloat(String(row[columnMapping.startLng] ?? ''));
    const endLat = parseFloat(String(row[columnMapping.endLat] ?? ''));
    const endLng = parseFloat(String(row[columnMapping.endLng] ?? ''));
    const standardSeats = parseInt(String(row[columnMapping.standardSeats] ?? '1'), 10) || 1;
    const wheelchairSeats = parseInt(String(row[columnMapping.wheelchairSeats] ?? '0'), 10) || 0;
    const numberOfVehicles = parseInt(String(row[columnMapping.numberOfVehicles] ?? '1'), 10) || 1;

    // Excel stores typed times as day fractions (06:00 -> 0.25), so normalise to HH:MM
    const shiftStart = toHHMM(row[columnMapping.shiftStart]) || '06:00';
    const shiftEnd = toHHMM(row[columnMapping.shiftEnd]) || '18:00';
    const breakStart = toHHMM(row[columnMapping.breakStart]);
    const breakEnd = toHHMM(row[columnMapping.breakEnd]);
    const breakLat = parseFloat(String(row[columnMapping.breakLat] ?? ''));
    const breakLng = parseFloat(String(row[columnMapping.breakLng] ?? ''));
    const breakLocation = (!isNaN(breakLat) && !isNaN(breakLng)) ? `${breakLat}, ${breakLng}` : '';

    const breaks = (breakStart && breakEnd) ? [{ start: breakStart, end: breakEnd, location: breakLocation }] : [];

    vehicles.push({
      id: uuidv4(),
      name: vehicleId,
      standardSeats,
      wheelchairSeats,
      startLocation: `${startLat}, ${startLng}`,
      endLocation: `${endLat}, ${endLng}`,
      startLocationCoordinates: !isNaN(startLat) && !isNaN(startLng) ? { lat: startLat, lng: startLng } : undefined,
      endLocationCoordinates: !isNaN(endLat) && !isNaN(endLng) ? { lat: endLat, lng: endLng } : undefined,
      numberOfVehicles: numberOfVehicles > 1 ? numberOfVehicles : undefined,
      operatingHours: [{
        start: shiftStart,
        end: shiftEnd,
        breaks,
      }],
    });
  });

  return vehicles;
}

function processPointsFile(
  fileEntry: { parsedRows: Record<string, unknown>[]; columnMapping: Record<string, string> },
  t: (key: string, opts?: any) => string
): Point[] {
  const points: Point[] = [];
  const { parsedRows, columnMapping } = fileEntry;

  parsedRows.forEach((row, idx) => {
    const lat = parseFloat(String(row[columnMapping.latitude] ?? ''));
    const lng = parseFloat(String(row[columnMapping.longitude] ?? ''));
    if (isNaN(lat) || isNaN(lng)) return;

    const name = String(row[columnMapping.name] ?? '') || `Point ${idx + 1}`;
    const description = String(row[columnMapping.description] ?? '');

    points.push({
      id: uuidv4(),
      type: 'point',
      name,
      description,
      colour: '#3388ff',
      icon: 'default',
      size: 24,
      isLocked: false,
      coordinates: [lat, lng],
    });
  });

  return points;
}

function processPolygonsFile(
  fileEntry: { parsedRows: Record<string, unknown>[]; columnMapping: Record<string, string> }
): Polygon[] {
  const polygons: Polygon[] = [];
  const { parsedRows, columnMapping } = fileEntry;

  parsedRows.forEach((row, idx) => {
    const name = String(row[columnMapping.name] ?? '') || `Polygon ${idx + 1}`;
    const coords = parseWktPolygonRing(String(row[columnMapping.wkt] ?? ''));
    if (!coords) return;

    polygons.push({
      id: uuidv4(),
      type: 'polygon',
      name,
      description: '',
      colour: '#3388ff',
      opacity: '0.5',
      borderColour: '#3388ff',
      borderWidth: 2,
      isLocked: false,
      coordinates: [coords],
      createdAt: Date.now(),
    } as Polygon);
  });

  return polygons;
}

export default ImportWizardModal;
